import React, { useState } from 'react';
import { apiFetchAuthed } from '../../apiHelpers';
import useChatStore from '../../stores/useChatStore';

export interface FlerpieNote {
  to_user_id: string;
  to_label: string;
  body: string;
  sent?: boolean;
}

/**
 * The answer to "want me to send that to Austin?". The server stored the
 * recipient and the text on the answer; this only shows them and waits for
 * the click. Nothing is sent until then, and a second click is a no-op.
 */
const FlerpieNoteButton = ({ messageId, note }: { messageId: string; note: FlerpieNote }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentHere, setSentHere] = useState(false);
  const sent = !!note.sent || sentHere;

  const send = async () => {
    if (sent || busy) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetchAuthed('/ai/notes/send', { method: 'POST', body: JSON.stringify({ message_id: messageId }) });
      setSentHere(true);
      useChatStore.getState().setMessageNote(messageId, { ...note, sent: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(/limit|any more/i.test(message) ? message : 'That did not send. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <div className="text-[11px] text-gray-400 italic mb-1.5 pl-2 border-l-2 border-primary/30">{note.body}</div>
      <button
        type="button"
        onClick={send}
        disabled={sent || busy}
        aria-label={sent ? `Sent to ${note.to_label}` : `Send to ${note.to_label}`}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium
                   bg-primary/15 text-primary hover:bg-primary/25 transition-colors duration-200
                   disabled:opacity-60 disabled:hover:bg-primary/15"
      >
        {sent ? 'Sent to' : 'Send to'}
        <span className="opacity-80 font-normal">{note.to_label}</span>
        {sent ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 6H10M6 2L10 6L6 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {error && <div className="mt-1 text-[10px] text-red-300">{error}</div>}
    </div>
  );
};

export default FlerpieNoteButton;
